/**
 * The numbers the other end's system needs to recognise the shipment: a
 * reference number, or for Amazon the two ids they use instead, and a booth
 * number when the freight is going to a trade show.
 *
 * A reference number is optional almost everywhere and mandatory at large
 * distribution warehouses, which is why the heading changes rather than the
 * section disappearing.
 *
 * Sections are numbered for the user, so how many of them render has to be
 * known by the screen before it numbers the section that follows —
 * `bookShipmentAddressReferenceSectionCount` is how it asks.
 */

import { AmazonReferenceIdQuestionBubble } from "@/Shared/ReferenceNumbers/Bubbles/AmazonReferenceIdQuestionBubble";
import { AmazonShipmentIdQuestionBubble } from "@/Shared/ReferenceNumbers/Bubbles/AmazonShipmentIdQuestionBubble";
import { BoothNumberQuestionBubble } from "@/Shared/ReferenceNumbers/Bubbles/BoothNumberQuestionBubble";
import { ReferenceNumberQuestionBubble } from "@/Shared/ReferenceNumbers/Bubbles/ReferenceNumberQuestionBubble";
import { useReferenceNumbersValidate } from "@/Shared/ReferenceNumbers/Hooks/useReferenceNumbersValidate";
import { ReferenceNumbersInput } from "@/Shared/ReferenceNumbers/Inputs/ReferenceNumbersInput";
import type { PreQuoteLocation } from "@freightsimple/generated-dashboard-openapi-client/models/PreQuoteLocation";
import { LocationContext } from "@freightsimple/generated-dashboard-openapi-client/models/LocationContext";
import { LocationType } from "@freightsimple/generated-dashboard-openapi-client/models/LocationType";
import { FormItem } from "@freightsimple/ui-core/FormLayout/FormItem";
import { Panel } from "@freightsimple/ui-core/PageLayout/Panel";
import { ShowFormErrors } from "@freightsimple/ui-core/FormLayout/ShowFormErrors";
import { TextInput } from "@freightsimple/ui-core/Controls/TextInput";
import { T } from "@freightsimple/ui-core/Typography/T";
import BookShipmentSmallSection from "../BookShipmentSmallSection";
import type { useBookShipmentAddressValidation } from "../../Hooks/useBookShipmentAddressValidation";
import type { useBookShipmentAmazonReference } from "../../Hooks/useBookShipmentAmazonReference";

export function bookShipmentAddressReferenceSectionCount(
  isAmazonDelivery: boolean,
  location: PreQuoteLocation,
): number {
  const referenceSections = isAmazonDelivery ? 2 : 1;
  const boothSections =
    location.locationType === LocationType.TradeShow ? 1 : 0;
  return referenceSections + boothSections;
}

interface BookShipmentAddressReferenceSectionsProps {
  /** Number of the first section rendered here; the rest follow it. */
  startIndex: number;
  context: LocationContext;
  displayName: string;
  pluralDisplayName: string;
  location: PreQuoteLocation;
  shipmentId: string;
  originalReferenceNumber: string;
  referenceNumber: string;
  onReferenceNumberChange: (referenceNumber: string) => void;
  boothNumber: string;
  onBoothNumberChange: (boothNumber: string) => void;
  forceValidation: boolean;
  validation: ReturnType<typeof useBookShipmentAddressValidation>;
  amazon: ReturnType<typeof useBookShipmentAmazonReference>;
}

// oxlint-disable-next-line complexity -- grandfathered, see ENG-7672
export function BookShipmentAddressReferenceSections(
  props: BookShipmentAddressReferenceSectionsProps,
) {
  const {
    startIndex,
    context,
    displayName,
    location,
    referenceNumber,
    forceValidation,
    validation,
    amazon,
  } = props;

  const referenceNumberValidation = useReferenceNumbersValidate({
    locationContext: context,
    originalReferenceNumber: props.originalReferenceNumber,
    shipmentId: props.shipmentId,
  });

  const boothIndex = startIndex + (amazon.isAmazonDelivery ? 2 : 1);
  const legacyReference =
    props.originalReferenceNumber.trim() !== "" &&
    amazon.amazonReferenceId === "" &&
    amazon.amazonShipmentId === ""
      ? props.originalReferenceNumber
      : undefined;

  return (
    <>
      {amazon.isAmazonDelivery ? (
        <>
          <BookShipmentSmallSection
            header={
              <>
                Amazon Reference ID
                <AmazonReferenceIdQuestionBubble displayName={"Delivery"} />
              </>
            }
            description={
              "Enter the Amazon reference number provided by the receiver for tracking purposes."
            }
            index={startIndex}
          >
            <Panel collapseOnNarrow>
              <ShowFormErrors show={forceValidation}>
                <FormItem
                  label="Enter Amazon Reference ID"
                  error={amazon.amazonReferenceIdError()}
                  fillWidth
                >
                  <TextInput
                    width="full"
                    value={amazon.amazonReferenceId}
                    onChange={(e) =>
                      amazon.setAmazonReferenceId(e.target.value)
                    }
                    maxLength={30}
                    data-testid="amazon-reference-id-input"
                  />
                </FormItem>
              </ShowFormErrors>
            </Panel>
          </BookShipmentSmallSection>
          <div className="h-8" />
          <BookShipmentSmallSection
            header={
              <>
                Amazon Shipment ID
                <AmazonShipmentIdQuestionBubble displayName={"Delivery"} />
              </>
            }
            description={
              "Enter the Amazon shipment ID provided by the receiver for their internal tracking system."
            }
            index={startIndex + 1}
          >
            <Panel collapseOnNarrow>
              <ShowFormErrors show={forceValidation}>
                <FormItem
                  label="Enter Amazon Shipment ID"
                  error={amazon.amazonShipmentIdError()}
                  fillWidth
                >
                  <TextInput
                    width="full"
                    value={amazon.amazonShipmentId}
                    onChange={(e) => amazon.setAmazonShipmentId(e.target.value)}
                    maxLength={30}
                    data-testid="amazon-shipment-id-input"
                  />
                </FormItem>
              </ShowFormErrors>
              {/* A reference typed before the two ids existed cannot be unpacked
                  into them; keep it in view so the customer can transcribe it. */}
              {legacyReference !== undefined && (
                <T
                  color="secondary"
                  size="sm"
                  data-testid="amazon-legacy-reference-hint"
                >
                  Previously entered reference: {legacyReference}
                </T>
              )}
            </Panel>
          </BookShipmentSmallSection>
        </>
      ) : (
        <BookShipmentSmallSection
          header={
            <>
              {context === LocationContext.Pickup && `Pickup Reference Number`}
              {context === LocationContext.Delivery &&
                `Delivery Reference Number`}
              {!validation.isReferenceNumberRequired() && (
                <span className="ml-2 text-sm font-normal text-content-muted">
                  Optional
                </span>
              )}
              <ReferenceNumberQuestionBubble displayName={displayName} />
            </>
          }
          description={`If you have a reference number for the ${
            context === LocationContext.Pickup ? `shipper's` : `receiver's`
          } system, enter it here.`}
          index={startIndex}
        >
          <Panel collapseOnNarrow>
            <ReferenceNumbersInput
              validation={referenceNumberValidation}
              label="Enter reference number"
              width="xl"
              value={referenceNumber}
              onChange={props.onReferenceNumberChange}
              maxLength={256}
              errorMessage={
                validation.isReferenceNumberRequired()
                  ? validation.referenceNumberError()
                  : undefined
              }
              forceValidation={
                validation.isReferenceNumberRequired()
                  ? forceValidation
                  : undefined
              }
              data-testid="reference-number-input"
            />
          </Panel>
        </BookShipmentSmallSection>
      )}
      {location.locationType === LocationType.TradeShow && (
        <>
          <div className="h-8" />
          <BookShipmentSmallSection
            header={
              <>
                {context === LocationContext.Pickup &&
                  `Tradeshow Pickup Booth Number`}
                {context === LocationContext.Delivery &&
                  `Tradeshow Delivery Booth Number`}
                <BoothNumberQuestionBubble displayName={displayName} />
              </>
            }
            description={`Booth number is required when dealing with trade show ${props.pluralDisplayName}`}
            index={boothIndex}
          >
            <Panel collapseOnNarrow>
              <ShowFormErrors show={forceValidation}>
                <FormItem
                  label="Enter booth number"
                  error={validation.boothErrorMessage}
                  fillWidth
                >
                  <TextInput
                    width="full"
                    value={props.boothNumber}
                    onChange={(e) => props.onBoothNumberChange(e.target.value)}
                    maxLength={256}
                    data-testid="booth-number-input"
                  />
                </FormItem>
              </ShowFormErrors>
            </Panel>
          </BookShipmentSmallSection>
        </>
      )}
    </>
  );
}
